/**
 * /admin/clientes/catalogos — Configurações › Clientes (CA3).
 *
 * Central de Catálogos do módulo Clientes: gestão CRUD (criar/renomear/
 * desativar) dos catálogos que a migration 400 seedou SEM tela + a
 * antecedência dos alertas de contrato (mig 401). Papéis de contato e
 * Segmentos já têm gestão em Catálogos › Avançados — aqui só há um atalho
 * para eles (não duplicamos).
 *
 * Admin-only (a casca /admin já barra não-admin). As escritas são guardadas
 * por `requireAdmin` no backend.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  HelpTip,
  Input,
  Label,
  PageHeader,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsList,
  TabsTrigger,
  ErroCampo,
  useToast,
} from '@/components/ui';
import { ComercialIcon } from '@/components/icons';
// V-1: validação que fala, em vez de botão desabilitado.
import { useValidacaoForm } from '@/lib/validacao-form';
import { useAbaNaUrl } from '@/lib/use-aba-na-url';
import {
  useContratoCatalogo,
  useContratoCatalogoActions,
  useAlertaContratoConfig,
  useAlertaContratoConfigActions,
  type CatalogoContratoSlug,
  type ContratoCatalogoItem,
  type ContratoCatalogoInput,
  type SlaUnidade,
} from '@/lib/contrato-catalogos-store';
import { textoDoPrazo } from '@/lib/contratos/texto-do-prazo';

import { PlantaoConfigPanel } from './PlantaoConfigPanel';

// ─── Config declarativa dos catálogos ─────────────────────────────────────

type ExtraField =
  | { kind: 'int'; api: 'horasUteis'; label: string; help: string; placeholder?: string }
  | { kind: 'bool'; api: 'escopoDetalhado'; label: string; help: string };

interface CatalogoConfig {
  slug: CatalogoContratoSlug;
  singular: string;
  plural: string;
  help: string;
  extra?: ExtraField;
}

const CATALOGOS: CatalogoConfig[] = [
  {
    slug: 'empresas-parceiras',
    singular: 'empresa parceira',
    plural: 'empresas parceiras',
    help: 'Empresas da relação do contrato além da IGM (que é a opção padrão). Ex.: Route.',
  },
  {
    slug: 'indices-reajuste',
    singular: 'índice de reajuste',
    plural: 'índices de reajuste',
    help: 'Índices usados no reajuste anual do contrato (IGP-M, IPCA, INPC…).',
  },
  {
    slug: 'slas',
    singular: 'tempo de atendimento',
    plural: 'tempos de atendimento',
    help: 'Prazos de atendimento (SLA). Cada opção tem um nome e um prazo, contado em horas de expediente ou em dias úteis, pelo Horário de funcionamento.',
    extra: {
      kind: 'int',
      api: 'horasUteis',
      label: 'Prazo',
      help: 'O número do prazo, na unidade escolhida acima (ex.: 8 horas de expediente, ou 1 dia útil).',
      placeholder: '8',
    },
  },
  {
    slug: 'periodicidades',
    singular: 'periodicidade',
    plural: 'periodicidades',
    help: 'Frequência das visitas preventivas (Semanal, Mensal, Bimestral…).',
  },
  {
    slug: 'tipos',
    singular: 'tipo de contrato',
    plural: 'tipos de contrato',
    help: 'Tipos de contrato de manutenção (Manutenção Mensal, Monitoramento, Portaria, Software, Drone, Locação…).',
    extra: {
      kind: 'bool',
      api: 'escopoDetalhado',
      label: 'Escopo detalhado',
      help: 'Ligue quando o tipo usa o formulário completo de escopo por subsistema (como a Manutenção Mensal).',
    },
  },
];

type TabKey = CatalogoContratoSlug | 'alertas' | 'plantao' | 'papeis';

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'empresas-parceiras', label: 'Empresas parceiras' },
  { key: 'indices-reajuste', label: 'Índices de reajuste' },
  { key: 'slas', label: 'Tempos de atendimento' },
  { key: 'periodicidades', label: 'Periodicidades' },
  { key: 'tipos', label: 'Tipos de contrato' },
  { key: 'alertas', label: 'Alertas' },
  // FECHAMENTO-DO-PLANTAO (06/10/2026): a tabela da Route, o limite do mês e os motivos de não cobrar.
  { key: 'plantao', label: 'Plantão' },
  { key: 'papeis', label: 'Papéis de contato' },
];

export function ConfiguracoesClientesPage() {
  // E: aba na URL. As abas saem do próprio TABS — lista nova entra sozinha.
  const { aba: tab, setAba: setTab } = useAbaNaUrl<TabKey>({
    abas: TABS.map((t) => t.key),
  });
  const catalogo = CATALOGOS.find((c) => c.slug === tab);

  return (
    <section className="min-w-0" data-testid="config-clientes-page">
      <PageHeader
        crumb="Configurações · Clientes"
        title={
          <span className="flex flex-wrap items-center gap-2">
            Catálogos de contrato
            <HelpTip texto="As listas que aparecem ao cadastrar um contrato (empresas parceiras, índices, prazos, periodicidades e tipos) e com quanta antecedência os alertas chegam. Você cria, renomeia e desativa sem precisar de uma nova versão do sistema." />
          </span>
        }
        subtitle="Desativar não apaga nada: o item some das novas seleções, mas os contratos que já usavam continuam do mesmo jeito."
      />

      {/* FASE-C: as 7 pílulas com `flex-wrap` quebravam em 3 linhas no 375px —
          agora é o Tabs canônico, 1 linha rolável (mesmos rótulos/ordem/testids). */}
      <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)} className="mb-6">
        <TabsList>
          {TABS.map((t) => (
            <TabsTrigger
              key={t.key}
              value={t.key}
              data-testid={`config-clientes-tab-${t.key}`}
            >
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {catalogo && <CatalogoPanel config={catalogo} />}
      {tab === 'alertas' && <AlertasPanel />}
      {/* Só admin chega aqui (a casca /admin barra os outros, e o servidor confere de novo em cada escrita). */}
      {tab === 'plantao' && <PlantaoConfigPanel />}
      {tab === 'papeis' && <PapeisAtalho />}
    </section>
  );
}

// ─── Panel genérico de um catálogo ─────────────────────────────────────────

function CatalogoPanel({ config }: { config: CatalogoConfig }) {
  const { data, isLoading } = useContratoCatalogo(config.slug, { includeInactive: true });
  const { create, update, remove } = useContratoCatalogoActions(config.slug);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ContratoCatalogoItem | null>(null);
  const rows = useMemo(() => data ?? [], [data]);

  function closeDialog() {
    setOpen(false);
    setEditing(null);
  }

  function handleSave(input: ContratoCatalogoInput) {
    if (editing) {
      update.mutate({ id: editing.id, input }, { onSuccess: closeDialog });
    } else {
      create.mutate(input, { onSuccess: closeDialog });
    }
  }

  return (
    <Card variant="flat">
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-line dark:border-line-dark">
        <p className="flex min-w-0 items-center gap-2 text-sm text-ink-2 dark:text-ink-dark-2">
          <span className="min-w-0 break-words">
            {rows.length} {rows.length === 1 ? config.singular : config.plural}
          </span>
          <HelpTip texto={config.help} />
        </p>
        <Button
          size="sm"
          data-testid={`config-clientes-new-${config.slug}`}
          onClick={() => {
            setEditing(null);
            setOpen(true);
          }}
        >
          + Novo
        </Button>
      </div>

      {isLoading ? (
        <p className="p-4 text-sm text-ink-3 dark:text-ink-dark-3">Carregando…</p>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<ComercialIcon className="w-6 h-6" />}
          title={`Nenhum item em ${config.plural}`}
          description="Adicione o primeiro pra começar."
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              {config.extra && (
                <TableHead className={config.extra.kind === 'int' ? 'text-center' : ''}>
                  {config.extra.label}
                </TableHead>
              )}
              <TableHead className="text-center">Ordem</TableHead>
              <TableHead className="text-center">Ativo</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`config-clientes-row-${r.id}`}>
                <TableCell
                  className={`min-w-0 break-words ${r.ativo ? '' : 'opacity-50'}`}
                >
                  {r.nome}
                </TableCell>
                {config.extra && (
                  <TableCell
                    className={config.extra.kind === 'int' ? 'text-center font-mono' : ''}
                  >
                    {config.extra.kind === 'int'
                      ? textoDoPrazo(r.horasUteis, r.unidade)
                      : r.escopoDetalhado
                        ? 'Sim'
                        : 'Não'}
                  </TableCell>
                )}
                <TableCell className="text-center font-mono">{r.ordem}</TableCell>
                <TableCell className="text-center">
                  <Switch
                    checked={r.ativo}
                    onChange={() => update.mutate({ id: r.id, input: { ativo: !r.ativo } })}
                    aria-label={`Ligar/desligar ${r.nome}`}
                  />
                </TableCell>
                <TableCell className="whitespace-nowrap text-right">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEditing(r);
                      setOpen(true);
                    }}
                  >
                    Editar
                  </Button>
                  {r.ativo && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => remove.mutate(r.id)}
                      className="text-red"
                    >
                      Desativar
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <CatalogoDialog
        open={open}
        config={config}
        editing={editing}
        onClose={closeDialog}
        onSubmit={handleSave}
        busy={create.isPending || update.isPending}
      />
    </Card>
  );
}

function CatalogoDialog({
  open,
  config,
  editing,
  onClose,
  onSubmit,
  busy,
}: {
  open: boolean;
  config: CatalogoConfig;
  editing: ContratoCatalogoItem | null;
  onClose: () => void;
  onSubmit: (input: ContratoCatalogoInput) => void;
  busy?: boolean;
}) {
  const [nome, setNome] = useState('');
  const [ordem, setOrdem] = useState(0);
  const [horasUteis, setHorasUteis] = useState(0);
  // A1 (06/10/2026): a unidade do prazo. Sem ela, salvar "1 dia útil" o transformava em horas.
  const [unidade, setUnidade] = useState<SlaUnidade>('horas_expediente');
  const [escopoDetalhado, setEscopoDetalhado] = useState(false);

  // Seed on open (mesmo padrão dos diálogos de Catálogos › Avançados).
  useMemo(() => {
    if (open) {
      setNome(editing?.nome ?? '');
      setOrdem(editing?.ordem ?? 0);
      setHorasUteis(editing?.horasUteis ?? 0);
      setUnidade(editing?.unidade ?? 'horas_expediente');
      setEscopoDetalhado(editing?.escopoDetalhado ?? false);
    }
  }, [open, editing]);

  const extra = config.extra;
  // V-1: as duas condições do antigo `podeSalvar` viraram regras com mensagem —
  // a validação não sumiu, ganhou voz.
  const toast = useToast();
  const validacao = useValidacaoForm((m) => toast.show(m, { variant: 'error' }));

  function submit() {
    const input: ContratoCatalogoInput = { nome: nome.trim(), ordem };
    if (extra?.kind === 'int') {
      input.horasUteis = horasUteis;
      input.unidade = unidade;
    }
    if (extra?.kind === 'bool') input.escopoDetalhado = escopoDetalhado;
    onSubmit(input);
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()} size="sm">
      <DialogHeader>
        <DialogTitle>
          {editing ? `Editar: ${editing.nome}` : `Novo item — ${config.singular}`}
        </DialogTitle>
      </DialogHeader>
      <DialogContent>
        <div className="space-y-3">
          <div>
            <Label htmlFor="cc-nome" required>
              Nome
            </Label>
            <Input
              id="cc-nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder={config.singular}
              aria-invalid={!!validacao.erroDe('cc-nome')}
            />
            <ErroCampo id="cc-nome" mensagem={validacao.erroDe('cc-nome')} />
          </div>

          {extra?.kind === 'int' && (
            <div className="space-y-3">
              <div>
                <Label htmlFor="cc-unidade">Contado em</Label>
                <Select
                  id="cc-unidade"
                  value={unidade}
                  onChange={(e) => setUnidade(e.target.value as SlaUnidade)}
                  data-testid="config-clientes-unidade"
                >
                  <option value="horas_expediente">Horas de expediente</option>
                  <option value="dias_uteis">Dias úteis</option>
                </Select>
                <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-1">
                  {unidade === 'dias_uteis'
                    ? 'Vence na mesma hora, no dia útil seguinte: aberto sexta às 16h com 1 dia útil, vence segunda às 16h.'
                    : 'Conta só as horas dentro do horário de funcionamento.'}
                </p>
              </div>
              <div>
                <Label htmlFor="cc-horas" required>
                  {unidade === 'dias_uteis' ? 'Dias úteis' : extra.label}
                </Label>
                <Input
                  id="cc-horas"
                  type="number"
                  min={1}
                  value={horasUteis}
                  onChange={(e) => setHorasUteis(Number(e.target.value) || 0)}
                  placeholder={extra.placeholder}
                />
                <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-1">{extra.help}</p>
              </div>
            </div>
          )}

          {extra?.kind === 'bool' && (
            <div className="flex items-center justify-between gap-3 p-3 border border-line dark:border-line-dark rounded-md">
              <div className="min-w-0">
                <p className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
                  {extra.label}
                </p>
                <p className="text-xs text-ink-3 dark:text-ink-dark-3">{extra.help}</p>
              </div>
              <Switch
                checked={escopoDetalhado}
                onChange={() => setEscopoDetalhado(!escopoDetalhado)}
                aria-label="Ligar/desligar escopo detalhado"
              />
            </div>
          )}

          <div>
            <Label htmlFor="cc-ordem">Ordem</Label>
            <Input
              id="cc-ordem"
              type="number"
              value={ordem}
              onChange={(e) => setOrdem(Number(e.target.value) || 0)}
            />
            <p className="text-xs text-ink-3 dark:text-ink-dark-3 mt-1">
              Posição do item nas listas (menor aparece primeiro).
            </p>
          </div>
        </div>
      </DialogContent>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          Cancelar
        </Button>
        {/* V-1: era `disabled={!podeSalvar}` — o botão apagava e nada dizia se
            faltava o nome ou se o número de horas estava fora do aceito. */}
        <Button
          loading={busy}
          onClick={() => {
            const ok = validacao.validar([
              {
                id: 'cc-nome',
                ok: nome.trim().length >= 1,
                mensagem: `Informe o nome ${config.singular ? `d${config.singular.match(/^[aeiou]/i) ? 'a' : 'o'} ${config.singular.toLocaleLowerCase('pt-BR')}` : 'do item'}.`,
              },
              {
                id: 'cc-horas',
                ok: !(extra?.kind === 'int' && horasUteis < 1),
                mensagem:
                  unidade === 'dias_uteis'
                    ? 'O prazo precisa ser de pelo menos 1 dia útil.'
                    : 'O prazo precisa ser de pelo menos 1 hora.',
              },
            ]);
            if (ok) submit();
          }}
          data-testid="config-clientes-save"
        >
          {editing ? 'Salvar' : 'Criar'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

// ─── Antecedência dos alertas ──────────────────────────────────────────────

function AlertasPanel() {
  const { data, isLoading } = useAlertaContratoConfig();
  const { save } = useAlertaContratoConfigActions();

  const [ativo, setAtivo] = useState(true);
  const [reajuste, setReajuste] = useState(45);
  const [avisoPrevio, setAvisoPrevio] = useState(15);

  // Seed quando os dados chegam / mudam.
  useMemo(() => {
    if (data) {
      setAtivo(data.ativo);
      setReajuste(data.reajusteAntecedenciaDias);
      setAvisoPrevio(data.avisoPrevioAntecedenciaDias);
    }
  }, [data]);

  if (isLoading) {
    return (
      <Card variant="flat">
        <p className="p-4 text-sm text-ink-3 dark:text-ink-dark-3">Carregando…</p>
      </Card>
    );
  }

  return (
    <Card variant="flat">
      <div className="flex flex-wrap items-center gap-2 p-4 border-b border-line dark:border-line-dark">
        <p className="min-w-0 text-sm text-ink-2 dark:text-ink-dark-2">
          Antecedência dos alertas
        </p>
        <HelpTip texto="Com quantos dias úteis de antecedência o sistema avisa (no sino) o administrador e o vendedor da carteira sobre o reajuste chegando e o fim do aviso prévio. A chave liga e desliga todos os alertas de contrato." />
      </div>
      <div className="max-w-md min-w-0 space-y-4 p-4">
        <div className="flex items-center justify-between gap-3 p-3 border border-line dark:border-line-dark rounded-md">
          <div className="min-w-0">
            <p className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">Alertas ligados</p>
            <p className="text-xs text-ink-3 dark:text-ink-dark-3">
              Desligado, nenhuma notificação de contrato é enviada.
            </p>
          </div>
          <Switch
            checked={ativo}
            onChange={() => setAtivo(!ativo)}
            aria-label="Ligar/desligar alertas de contrato"
          />
        </div>

        <div>
          <Label htmlFor="cc-reajuste">Reajuste — antecedência (dias úteis)</Label>
          <Input
            id="cc-reajuste"
            type="number"
            min={1}
            max={365}
            value={reajuste}
            onChange={(e) => setReajuste(Number(e.target.value) || 0)}
          />
        </div>

        <div>
          <Label htmlFor="cc-aviso">Fim do aviso prévio — antecedência (dias úteis)</Label>
          <Input
            id="cc-aviso"
            type="number"
            min={1}
            max={365}
            value={avisoPrevio}
            onChange={(e) => setAvisoPrevio(Number(e.target.value) || 0)}
          />
        </div>

        <div className="flex justify-end">
          <Button
            loading={save.isPending}
            disabled={reajuste < 1 || avisoPrevio < 1}
            data-testid="config-clientes-alertas-save"
            onClick={() =>
              save.mutate({
                ativo,
                reajusteAntecedenciaDias: reajuste,
                avisoPrevioAntecedenciaDias: avisoPrevio,
              })
            }
          >
            Salvar
          </Button>
        </div>
      </div>
    </Card>
  );
}

// ─── Atalho: Papéis de contato (gestão vive em Catálogos › Avançados) ──────

function PapeisAtalho() {
  return (
    <Card variant="flat">
      <div className="min-w-0 space-y-3 p-6">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-lg font-semibold text-ink-1 dark:text-ink-dark-1">
            Papéis de contato
          </h3>
          <HelpTip texto="Define como cada contato se relaciona ao cliente (síndico, administradora…). A gestão já existe em Catálogos › Avançados — este é só o atalho, para não duplicar." />
        </div>
        <p className="text-sm text-ink-2 dark:text-ink-dark-2">
          Criar, renomear e desativar papéis de contato acontece em{' '}
          <strong>Catálogos › Avançados › Papéis de Vínculo</strong>. Use o botão
          abaixo para abrir.
        </p>
        <Link to="/admin/catalogos">
          <Button size="sm" data-testid="config-clientes-papeis-link">
            Abrir Catálogos avançados
          </Button>
        </Link>
      </div>
    </Card>
  );
}

export default ConfiguracoesClientesPage;
